import { Dropdown } from 'bootstrap'
import { onAllTransitionsEnded } from './utils/dom'

/**
 * This file enables adding an enter and leave animation to Bootstrap dropdowns.
 * To use it, just add css animations on opacity or other properties to the dropdown
 * that trigger on the .show class.
 */

// Get the associated dropdown-menu from a dropdown-link or button
export function getDropdownMenu(link: unknown) {
  if (!(link instanceof HTMLElement)) {
    return
  }
  const dropdown = Dropdown.getOrCreateInstance(link)

  // Use the private property to get the menu. Sadly bootstrap doesn't officially expose this
  if ('_menu' in dropdown && dropdown._menu instanceof HTMLElement) {
    return dropdown._menu
  }
}

// A map of abort-controllers to cancel setting display:none on animation end
// if the dropdown is shown again in the meantime
const abortHiddenEvents = new WeakMap<Element, AbortController>()

document.addEventListener('show.bs.dropdown', (e) => {
  const dropdownMenu = getDropdownMenu(e.target)
  if (!dropdownMenu) {
    return
  }

  // Cancel potential hiding of the dropdown on animation end and delete the now
  // unused abort-controller
  abortHiddenEvents.get(dropdownMenu)?.abort()
  abortHiddenEvents.delete(dropdownMenu)

  // Set the dropdown to display:block before the 'show'-class is added so that the
  // show-class can trigger an animation
  dropdownMenu.style.display = 'block'
})

document.addEventListener('hide.bs.dropdown', (e) => {
  const dropdownMenu = getDropdownMenu(e.target)
  if (!dropdownMenu) {
    return
  }
  // get the current transform position and keep it while closing
  const currentTransform = dropdownMenu.style.transform
  requestAnimationFrame(() => {
    dropdownMenu.style.transform = currentTransform
  })
})

document.addEventListener('hidden.bs.dropdown', (e) => {
  const dropdownMenu = getDropdownMenu(e.target)
  if (!dropdownMenu) {
    return
  }

  // Cancel potential hiding of the dropdown on animation end and delete the now
  // unused abort-controller
  abortHiddenEvents.get(dropdownMenu)?.abort()
  abortHiddenEvents.delete(dropdownMenu)

  // Remove display:block after the animation has finished. This event-listener is
  // cancellable using the abortEvent Controller
  const abortEvent = new AbortController()
  abortHiddenEvents.set(dropdownMenu, abortEvent)
  onAllTransitionsEnded(
    dropdownMenu,
    () => {
      dropdownMenu.style.display = ''

      // The abort-controller is no longer needed
      abortHiddenEvents.delete(dropdownMenu)
    },
    { signal: abortEvent.signal }
  )
})
